import { useState, useEffect } from "react";
import { safeHref } from "../lib/safeUrl";
import { useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import FoundingSpotsAdminCard from "../components/FoundingSpotsAdminCard";
import HygienaAdminPanel from "../components/HygienaAdminPanel";
import TaxonomyManager from "../components/TaxonomyManager";
import WhatsNewPublisher from "../components/WhatsNewPublisher";
import ExpandableSubmissionCard, { SubmissionStatusBadge, SubmissionSentBadge } from "../components/ExpandableSubmissionCard";
import PollsAdminPanel from "../components/PollsAdminPanel";
import ReportsQueuePanel from "../components/ReportsQueuePanel";
import { SecureDocButton, DocStatusNote } from "../components/SecureDocButton";
import { AdminEmailMigrationCard } from "../components/AdminEmailMigrationCard";
import { AccountPurgeReportCard } from "../components/AccountPurgeReportCard";
import { BackupsCard } from "../components/BackupsCard";
import { 
  Users, CheckCircle, XCircle, Clock, FileText, 
  BarChart3, ExternalLink, Shield, MessageCircle, Eye, EyeOff,
  AlertTriangle, Bell, ShieldOff, KeyRound, UserCog, Search, Crown, Star, Handshake, GraduationCap,
  Send, Mail, Copy, Trash2, Ticket, Plus, MessageSquare, ChevronDown, ChevronUp, Sparkles, MapPin
} from "lucide-react";

const PERMISSION_LABELS = {
  approve_verifications: "Approve/Reject Verifications",
  approve_posts: "Approve/Reject Posts",
  manage_bans: "Manage Bans",
  reset_passwords: "Reset Passwords",
  manage_roles: "Manage Roles",
  manage_members: "Manage Members",
  view_admin_stats: "View Admin Stats",
  view_alerts: "View Security Alerts",
};

const AdminPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [anonymousPosts, setAnonymousPosts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [clinicReviews, setClinicReviews] = useState([]);
  const [partnerApps, setPartnerApps] = useState([]);
  const [studentEligibility, setStudentEligibility] = useState([]);
  const [eligibilityFilter, setEligibilityFilter] = useState("submitted");
  const [betaInvites, setBetaInvites] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [inviteFilter, setInviteFilter] = useState("available");
  const [inviteForm, setInviteForm] = useState({ invitee_name: "", invitee_email: "", note: "" });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [waitlistSearch, setWaitlistSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "pending");
  const [reportCount, setReportCount] = useState(0);
  const fetchReportCount = () => axios.get(`${API}/admin/reports/count`).then(r => setReportCount(r.data.open || 0)).catch(() => {});
  const [contactQuestions, setContactQuestions] = useState([]);
  const [questionsFilter, setQuestionsFilter] = useState("new");
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [partnerInterest, setPartnerInterest] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState("new");
  const [expandedInterest, setExpandedInterest] = useState(null);
  // ---- Guest Professional accounts (founder-only) ----
  const [guestPros, setGuestPros] = useState([]);
  const [showGuestProModal, setShowGuestProModal] = useState(false);
  const [creatingGuestPro, setCreatingGuestPro] = useState(false);
  const [guestProForm, setGuestProForm] = useState({
    full_name: "", email: "", company_or_firm: "", reason: "",
    access_start_date: "", access_end_date: "", send_welcome_email: true,
  });
  // Result of last create: shown in a one-time inline panel so the founder
  // can copy the auto-generated password before closing the modal.
  const [lastCreatedGuestPro, setLastCreatedGuestPro] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showResetPwDialog, setShowResetPwDialog] = useState(false);
  const [roleForm, setRoleForm] = useState({ admin_role: "", permissions: [] });
  const [adminToggleTarget, setAdminToggleTarget] = useState(null); // { user, willBeAdmin }
  const [adminTogglePending, setAdminTogglePending] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, requestsRes, anonRes, notifsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/verification-requests`),
        axios.get(`${API}/admin/anonymous-posts`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/notifications?unread_only=false`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setVerificationRequests(requestsRes.data);
      setAnonymousPosts(anonRes.data);
      setNotifications(notifsRes.data);
      setAllUsers(usersRes.data);
      fetchReportCount();
      
      // Fetch audit log, clinic reviews, partner apps, and student-eligibility queue
      try {
        const [auditRes, clinicRes, partnerRes, eligRes, invitesRes, waitlistRes, questionsRes, pendingEventsRes, partnerInterestRes] = await Promise.all([
          axios.get(`${API}/admin/audit-log?limit=50`),
          axios.get(`${API}/admin/clinic-reviews`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/partner-applications`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/student-eligibility?status=submitted`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/beta-invites`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/waitlist`).catch(() => ({ data: { rows: [] } })),
          axios.get(`${API}/admin/contact-questions`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/events/pending`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/partner-interest`).catch(() => ({ data: [] })),
        ]);
        setAuditLogs(auditRes.data);
        setClinicReviews(clinicRes.data);
        setPartnerApps(partnerRes.data);
        setStudentEligibility(eligRes.data);
        setBetaInvites(Array.isArray(invitesRes.data) ? invitesRes.data : []);
        setWaitlist(Array.isArray(waitlistRes.data?.rows) ? waitlistRes.data.rows : []);
        // contact-questions + partner-interest were normalized to return bare lists
        // (matching admin.py siblings). Keep the {rows} fallback so a stale prod
        // deploy that hasn't picked up the backend change still works.
        setContactQuestions(
          Array.isArray(questionsRes.data) ? questionsRes.data
            : Array.isArray(questionsRes.data?.rows) ? questionsRes.data.rows : []
        );
        setPendingEvents(Array.isArray(pendingEventsRes.data) ? pendingEventsRes.data : []);
        setPartnerInterest(
          Array.isArray(partnerInterestRes.data) ? partnerInterestRes.data
            : Array.isArray(partnerInterestRes.data?.rows) ? partnerInterestRes.data.rows : []
        );
      } catch { /* swallow */ }
      // Founder-only: list guest professional accounts. Master admins get
      // 403 here, which we silently swallow so their console stays clean.
      try {
        const gpRes = await axios.get(`${API}/admin/guest-professionals`);
        setGuestPros(Array.isArray(gpRes.data) ? gpRes.data : []);
      } catch { /* expected for non-founders */ }
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (verificationId) => {
    setProcessingId(verificationId);
    try {
      await axios.post(`${API}/admin/verification-requests/${verificationId}/approve`);
      toast.success("Verification approved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve verification");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (verificationId) => {
    const notes = prompt("Enter rejection reason (optional):");
    setProcessingId(verificationId);
    try {
      await axios.post(`${API}/admin/verification-requests/${verificationId}/reject`, { notes });
      toast.success("Verification rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject verification");
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprovePost = async (discussionId) => {
    setProcessingId(discussionId);
    try {
      await axios.post(`${API}/admin/anonymous-posts/${discussionId}/approve`);
      toast.success("Anonymous post approved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve post");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectPost = async (discussionId) => {
    setProcessingId(discussionId);
    try {
      await axios.post(`${API}/admin/anonymous-posts/${discussionId}/reject`);
      toast.success("Anonymous post rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject post");
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveReview = async (reviewId) => {
    setProcessingId(reviewId);
    try {
      await axios.post(`${API}/admin/clinic-reviews/${reviewId}/approve`);
      toast.success("Review approved!");
      fetchData();
    } catch (error) { toast.error("Failed to approve"); }
    finally { setProcessingId(null); }
  };

  const handleRejectReview = async (reviewId) => {
    setProcessingId(reviewId);
    try {
      await axios.post(`${API}/admin/clinic-reviews/${reviewId}/reject`);
      toast.success("Review rejected");
      fetchData();
    } catch (error) { toast.error("Failed to reject"); }
    finally { setProcessingId(null); }
  };

  const handleApprovePartner = async (partnerId) => {
    setProcessingId(partnerId);
    try {
      await axios.post(`${API}/admin/partners/${partnerId}/approve`);
      toast.success("Partner approved!");
      fetchData();
    } catch (error) { toast.error("Failed to approve"); }
    finally { setProcessingId(null); }
  };

  const handleRejectPartner = async (partnerId) => {
    setProcessingId(partnerId);
    try {
      await axios.post(`${API}/admin/partners/${partnerId}/reject`);
      toast.success("Partner rejected");
      fetchData();
    } catch (error) { toast.error("Failed to reject"); }
    finally { setProcessingId(null); }
  };

  // ---- Pending Events ----
  const handleApproveEvent = async (eventId) => {
    setProcessingId(eventId);
    try {
      await axios.post(`${API}/admin/events/${eventId}/approve`);
      toast.success("Event approved — now live.");
      fetchData();
    } catch { toast.error("Failed to approve event"); }
    finally { setProcessingId(null); }
  };

  const handleRejectEvent = async (eventId) => {
    if (!window.confirm("Reject this event? It will be hidden from members.")) return;
    setProcessingId(eventId);
    try {
      await axios.post(`${API}/admin/events/${eventId}/reject`);
      toast.success("Event rejected");
      fetchData();
    } catch { toast.error("Failed to reject event"); }
    finally { setProcessingId(null); }
  };

  // ---- Partner Interest (public landing form) ----
  const handleMarkInterestReplied = async (interestId) => {
    setProcessingId(interestId);
    try {
      await axios.post(`${API}/admin/partner-interest/${interestId}/mark-replied`);
      toast.success("Marked as replied");
      fetchData();
    } catch { toast.error("Failed to mark replied"); }
    finally { setProcessingId(null); }
  };

  const handleDeleteInterest = async (interestId) => {
    if (!window.confirm("Delete this partner interest submission?")) return;
    setProcessingId(interestId);
    try {
      await axios.delete(`${API}/admin/partner-interest/${interestId}`);
      toast.success("Submission deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
    finally { setProcessingId(null); }
  };

  // ---- Guest Professional accounts (founder-only) ----
  const handleCreateGuestPro = async (e) => {
    e?.preventDefault?.();
    if (!guestProForm.full_name || !guestProForm.email || !guestProForm.company_or_firm || !guestProForm.reason) {
      toast.error("Please fill in name, email, company, and reason.");
      return;
    }
    setCreatingGuestPro(true);
    try {
      const payload = {
        full_name: guestProForm.full_name,
        email: guestProForm.email.toLowerCase().trim(),
        company_or_firm: guestProForm.company_or_firm,
        reason: guestProForm.reason,
        send_welcome_email: guestProForm.send_welcome_email,
      };
      if (guestProForm.access_start_date) payload.access_start_date = guestProForm.access_start_date;
      if (guestProForm.access_end_date) payload.access_end_date = guestProForm.access_end_date;
      const res = await axios.post(`${API}/admin/guest-professionals`, payload);
      setLastCreatedGuestPro(res.data);
      toast.success(`Guest Professional account created for ${res.data.full_name}`);
      // Reset form but keep modal open so the founder can copy the password.
      setGuestProForm({
        full_name: "", email: "", company_or_firm: "", reason: "",
        access_start_date: "", access_end_date: "", send_welcome_email: true,
      });
      // Refresh the list in the background.
      try {
        const gpRes = await axios.get(`${API}/admin/guest-professionals`);
        setGuestPros(Array.isArray(gpRes.data) ? gpRes.data : []);
      } catch { /* non-fatal */ }
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to create guest account";
      toast.error(detail);
    } finally {
      setCreatingGuestPro(false);
    }
  };

  const handleRevokeGuestPro = async (userId, name) => {
    if (!window.confirm(`Revoke access for ${name}? They won't be able to log in until you reinstate them.`)) return;
    try {
      await axios.post(`${API}/admin/guest-professionals/${userId}/revoke`);
      toast.success("Access revoked");
      const gpRes = await axios.get(`${API}/admin/guest-professionals?include_revoked=true`);
      setGuestPros(Array.isArray(gpRes.data) ? gpRes.data : []);
    } catch { toast.error("Failed to revoke"); }
  };

  const handleReinstateGuestPro = async (userId) => {
    try {
      await axios.post(`${API}/admin/guest-professionals/${userId}/reinstate`);
      toast.success("Access reinstated");
      const gpRes = await axios.get(`${API}/admin/guest-professionals?include_revoked=true`);
      setGuestPros(Array.isArray(gpRes.data) ? gpRes.data : []);
    } catch { toast.error("Failed to reinstate"); }
  };

  const handleDeleteGuestPro = async (userId, name) => {
    if (!window.confirm(`Permanently delete the Guest Professional account for ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/guest-professionals/${userId}`);
      toast.success("Account deleted");
      const gpRes = await axios.get(`${API}/admin/guest-professionals?include_revoked=true`);
      setGuestPros(Array.isArray(gpRes.data) ? gpRes.data : []);
    } catch { toast.error("Failed to delete"); }
  };

  // ---- Student Eligibility ----
  const refreshEligibility = async (filter = eligibilityFilter) => {
    try {
      const r = await axios.get(`${API}/admin/student-eligibility?status=${filter}`);
      setStudentEligibility(r.data);
    } catch { /* surfaced to user only on action below */ }
  };

  const handleApproveEligibility = async (userId) => {
    setProcessingId(userId);
    try {
      await axios.post(`${API}/admin/student-eligibility/${userId}/approve`);
      toast.success("Eligibility approved — they can now subscribe to Student tier.");
      await refreshEligibility();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectEligibility = async (userId, notes = "") => {
    setProcessingId(userId);
    try {
      await axios.post(`${API}/admin/student-eligibility/${userId}/reject`, { notes });
      toast.success("Eligibility rejected.");
      await refreshEligibility();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setProcessingId(null);
    }
  };

  const handleMarkNotifRead = async (notifId) => {
    try {
      await axios.post(`${API}/admin/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.notification_id === notifId ? { ...n, is_read: true } : n));
    } catch {}
  };

  const handleUnbanUser = async (userId) => {
    if (!window.confirm("Are you sure you want to unban this user?")) return;
    setProcessingId(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/unban`);
      toast.success("User has been unbanned");
      fetchData();
    } catch (error) {
      toast.error("Failed to unban user");
    } finally {
      setProcessingId(null);
    }
  };

  const openRoleDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setRoleForm({
      admin_role: targetUser.admin_role || "",
      permissions: targetUser.permissions || []
    });
    setShowRoleDialog(true);
  };

  const handleSetRole = async () => {
    if (!selectedUser) return;
    setProcessingId(selectedUser.user_id);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.user_id}/set-role`, roleForm);
      toast.success(`Role updated for ${selectedUser.name || selectedUser.email}`);
      setShowRoleDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set role");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveRole = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Remove admin role from ${selectedUser.name || selectedUser.email}?`)) return;
    setProcessingId(selectedUser.user_id);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.user_id}/set-role`, { admin_role: "", permissions: [] });
      toast.success("Admin role removed");
      setShowRoleDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove role");
    } finally {
      setProcessingId(null);
    }
  };

  // Founder-only: simple Admin ON/OFF toggle for any non-founder user
  const requestAdminToggle = (targetUser) => {
    const isCurrentlyAdmin = targetUser.admin_role === "admin" || targetUser.is_admin;
    setAdminToggleTarget({ user: targetUser, willBeAdmin: !isCurrentlyAdmin });
  };

  const confirmAdminToggle = async () => {
    if (!adminToggleTarget) return;
    const { user: t, willBeAdmin } = adminToggleTarget;
    setAdminTogglePending(true);
    setProcessingId(t.user_id);
    try {
      await axios.post(`${API}/admin/users/${t.user_id}/toggle-admin`);
      toast.success(willBeAdmin
        ? `${t.name || t.email} is now an admin`
        : `${t.name || t.email} is now a member`);
      setAdminToggleTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle admin");
    } finally {
      setAdminTogglePending(false);
      setProcessingId(null);
    }
  };

  const toggleModerateContent = async (t) => {
    setProcessingId(t.user_id);
    try {
      const res = await axios.post(`${API}/admin/users/${t.user_id}/toggle-moderate-content`);
      toast.success(res.data.can_moderate_content
        ? `${t.name || t.email} can now edit & delete members' content`
        : `${t.name || t.email} can no longer moderate members' content`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update moderation access");
    } finally {
      setProcessingId(null);
    }
  };

  const openResetPwDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setNewPassword("");
    setShowResetPwDialog(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setProcessingId(selectedUser.user_id);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.user_id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${selectedUser.email}`);
      setShowResetPwDialog(false);
      setSelectedUser(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setProcessingId(null);
    }
  };

  const togglePermission = (perm) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  // ---- Beta invites & waitlist ----
  const refreshInvites = async (filter = inviteFilter) => {
    try {
      const res = await axios.get(`${API}/admin/beta-invites`, { params: filter && filter !== "all" ? { status: filter } : {} });
      setBetaInvites(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to load beta invites");
    }
  };

  const refreshWaitlist = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist`);
      setWaitlist(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch {
      toast.error("Failed to load waitlist");
    }
  };

  const handleCreateInvite = async (e) => {
    e?.preventDefault?.();
    if (!inviteForm.invitee_name.trim() || !inviteForm.invitee_email.trim()) {
      toast.error("Name and email required");
      return;
    }
    setCreatingInvite(true);
    try {
      const res = await axios.post(`${API}/admin/beta-invites`, {
        invitee_name: inviteForm.invitee_name.trim(),
        invitee_email: inviteForm.invitee_email.trim().toLowerCase(),
        note: inviteForm.note.trim() || undefined,
      });
      toast.success(`Beta code ${res.data.code} created for ${res.data.invitee_name}`);
      // Copy generated code to clipboard for easy share
      try { await navigator.clipboard.writeText(res.data.code); toast.message("Code copied to clipboard"); } catch {}
      setInviteForm({ invitee_name: "", invitee_email: "", note: "" });
      setInviteFilter("available");
      refreshInvites("available");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invite");
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevokeInvite = async (code) => {
    if (!window.confirm(`Revoke beta code ${code}? This cannot be undone.`)) return;
    setProcessingId(code);
    try {
      await axios.post(`${API}/admin/beta-invites/${code}/revoke`);
      toast.success("Code revoked");
      refreshInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke");
    } finally {
      setProcessingId(null);
    }
  };

  const handleCopyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); }
    catch { toast.error("Could not copy to clipboard"); }
  };

  const handleInviteFromWaitlist = (row) => {
    setInviteForm({
      invitee_name: row.name || row.email.split("@")[0],
      invitee_email: row.email,
      note: row.referral_source ? `From waitlist · ${row.referral_source}` : "From waitlist",
    });
    // Switch to the Beta Invites tab so the generator form is mounted, then scroll.
    setActiveTab("beta-invites");
    setTimeout(() => {
      document.getElementById("beta-invite-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    toast.message(`Pre-filled invite form for ${row.email}`);
  };

  const handleRemoveFromWaitlist = async (email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    setProcessingId(email);
    try {
      await axios.delete(`${API}/admin/waitlist/${encodeURIComponent(email)}`);
      toast.success("Removed from waitlist");
      refreshWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove");
    } finally {
      setProcessingId(null);
    }
  };

  const filteredInvites = betaInvites.filter(inv => {
    if (inviteFilter === "all") return true;
    if (inviteFilter === "available") return !inv.used && !inv.revoked;
    if (inviteFilter === "used") return inv.used;
    if (inviteFilter === "revoked") return inv.revoked;
    return true;
  });

  const filteredWaitlist = waitlist.filter(w =>
    !waitlistSearch ||
    w.email?.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
    w.name?.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
    w.province?.toLowerCase().includes(waitlistSearch.toLowerCase())
  );
  const availableInviteCount = betaInvites.filter(i => !i.used && !i.revoked).length;

  // ---- Contact questions ----
  const refreshQuestions = async () => {
    try {
      const r = await axios.get(`${API}/admin/contact-questions`);
      setContactQuestions(
        Array.isArray(r.data) ? r.data
          : Array.isArray(r.data?.rows) ? r.data.rows : []
      );
    } catch {
      toast.error("Failed to load questions");
    }
  };
  const handleMarkReplied = async (qid) => {
    setProcessingId(qid);
    try {
      await axios.post(`${API}/admin/contact-questions/${qid}/mark-replied`);
      toast.success("Marked as replied");
      refreshQuestions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setProcessingId(null);
    }
  };
  const filteredQuestions = contactQuestions.filter(q =>
    questionsFilter === "all" || q.status === questionsFilter
  );
  const newQuestionsCount = contactQuestions.filter(q => q.status === "new").length;

  const filteredUsers = allUsers.filter(u =>
    !userSearch || 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );
  const isFounder = user?.admin_role === "founder";
  const canModerateContent = isFounder || (user?.is_admin && user?.can_moderate_content);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const pendingRequests = verificationRequests.filter(r => r.status === "submitted");
  const processedRequests = verificationRequests.filter(r => r.status !== "submitted");
  const unreadNotifs = notifications.filter(n => !n.is_read);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 lg:p-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#F0ECF7] rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-[#F0ECF7] rounded-2xl"></div>
              ))}
            </div>
            <div className="h-64 bg-[#F0ECF7] rounded-2xl"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 md:p-8 lg:p-12" data-testid="admin-page">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
            {(() => {
              const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
              const isNew = (iso) => iso && new Date(iso).getTime() >= cutoff;
              const newCount =
                waitlist.filter((w) => isNew(w.created_at)).length +
                contactQuestions.filter((q) => isNew(q.created_at) && q.status === "new").length +
                partnerInterest.filter((p) => isNew(p.created_at) && p.status !== "replied").length +
                pendingEvents.filter((e) => isNew(e.created_at)).length;
              if (newCount === 0) return null;
              return (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#6B4E8A] text-white text-xs font-medium shadow-sm"
                  data-testid="admin-new-activity-badge"
                  title="New activity in the last 7 days across waitlist, questions, partner interest, and events"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {newCount} new this week
                </span>
              );
            })()}
          </div>
          <p className="text-[#64748B] mt-1">
            Manage verifications and monitor community activity
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-8"
        >
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#6B4E8A]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#6B4E8A]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{stats?.total_users || 0}</p>
                <p className="text-xs text-[#64748B]">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#10B981]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{stats?.verified_users || 0}</p>
                <p className="text-xs text-[#64748B]">Verified</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#8DB5A0]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#8DB5A0]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{stats?.pending_verifications || 0}</p>
                <p className="text-xs text-[#64748B]">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#8DB5A0]/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#8DB5A0]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{stats?.total_discussions || 0}</p>
                <p className="text-xs text-[#64748B]">Discussions</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#F0ECF7]/50 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-[#8DB5A0]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{stats?.total_resources || 0}</p>
                <p className="text-xs text-[#64748B]">Resources</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Founding-Member Spots — admin view + manual adjustment controls */}
        <FoundingSpotsAdminCard />

        {/* Verification & Anonymous Posts */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-4 sm:p-6"
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Admin Management
                </h2>
                <a
                  href="/admin/tiers"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#6B4E8A] text-white text-xs font-medium hover:bg-[#553D6F] transition-colors"
                  data-testid="open-tier-management-link"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v10l9 4 9-4V7M3 7l9-4 9 4M3 7l9 4 9-4M12 11v10"/></svg>
                  Manage Tiers
                </a>
                <a
                  href="/admin/faq"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#8DB5A0] text-white text-xs font-medium hover:bg-[#7AA48F] transition-colors"
                  data-testid="open-faq-management-link"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"/></svg>
                  Manage FAQ
                </a>
                <a
                  href="/admin/content"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#D89B4E] text-white text-xs font-medium hover:bg-[#B8842F] transition-colors"
                  data-testid="open-content-management-link"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
                  Manage Content
                </a>
                {isFounder && (
                  <a
                    href="/admin/brand-legal"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#E07A5F] text-white text-xs font-medium hover:bg-[#C96A52] transition-colors"
                    data-testid="open-brand-legal-link"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/></svg>
                    Brand & Legal
                  </a>
                )}
              </div>
              {(() => {
                const tabs = [
                  { value: "pending", label: "Verifications", count: pendingRequests.length, testid: "verifications-tab" },
                  ...(canModerateContent ? [{ value: "reports", label: "Reports", count: reportCount, testid: "reports-tab" }] : []),
                  { value: "anonymous", label: "Anonymous", count: anonymousPosts.length, testid: "anonymous-posts-tab" },
                  { value: "alerts", label: "Alerts", count: unreadNotifs.length, testid: "alerts-tab" },
                  { value: "clinic-reviews", label: "Reviews", count: clinicReviews.length, testid: "clinic-reviews-tab" },
                  { value: "partners-pending", label: "Partners", count: partnerApps.length, testid: "partners-tab" },
                  { value: "events-pending", label: "Events", count: pendingEvents.length, testid: "events-pending-tab" },
                  { value: "partner-interest", label: "Partner Applications", count: partnerInterest.filter(p => p.status !== 'replied').length, testid: "partner-interest-tab" },
                  { value: "student-eligibility", label: "Student Verifs", count: studentEligibility.filter(s => s.student_eligibility_status === "submitted").length, testid: "student-eligibility-tab" },
                  { value: "beta-invites", label: "Beta Invites", count: availableInviteCount, testid: "beta-invites-tab" },
                  { value: "waitlist", label: "Waitlist", count: waitlist.length, testid: "waitlist-tab" },
                  { value: "contact-questions", label: "Contact Questions", count: newQuestionsCount, testid: "contact-questions-tab" },
                  { value: "users", label: "Users", count: allUsers.length, testid: "users-tab" },
                  ...(isFounder ? [{ value: "guest-pros", label: "Guest Pros", count: guestPros.filter(g => !g.guest_revoked).length, testid: "guest-pros-tab" }] : []),
                  { value: "audit", label: "Audit", count: null, testid: "audit-tab" },
                  { value: "processed", label: "Processed", count: null, testid: undefined },
                  { value: "hygiena", label: "Hygiena AI", count: null, testid: "hygiena-admin-tab" },
                  { value: "taxonomy", label: "Lists & Categories", count: null, testid: "taxonomy-admin-tab" },
                  { value: "whats_new", label: "What's New", count: null, testid: "whats-new-admin-tab" },
                  { value: "polls", label: "Polls", count: null, testid: "polls-admin-tab" },
                ];
                const activeTabObj = tabs.find((t) => t.value === activeTab) || tabs[0];
                return (
                  <>
                    {/* Mobile: native select for fast jumping (no horizontal scroll needed) */}
                    <div className="md:hidden" data-testid="admin-tabs-mobile">
                      <label className="text-[11px] uppercase tracking-wide text-[#94A3B8] mb-1.5 block">Section</label>
                      <select
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                        className="w-full h-11 px-3 pr-10 bg-[#F0ECF7] border border-[#E5DFEC] rounded-xl text-sm text-[#6B4E8A] font-medium appearance-none focus:outline-none focus:border-[#6B4E8A]"
                        aria-label="Admin section"
                        data-testid="admin-tabs-mobile-select"
                        style={{
                          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='%236B4E8A' stroke-width='2' viewBox='0 0 24 24'><polyline points='6 9 12 15 18 9' /></svg>\")",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 0.75rem center",
                          backgroundSize: "1rem",
                        }}
                      >
                        {tabs.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}{t.count !== null && t.count !== undefined ? ` (${t.count})` : ""}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-[#94A3B8] mt-1.5">Currently viewing: <strong className="text-[#6B4E8A]">{activeTabObj.label}</strong></p>
                    </div>

                    {/* Tablet & desktop: horizontal scroll tab list */}
                    <div
                      className="hidden md:block overflow-x-auto -mx-2 px-2 w-full min-w-0 scroll-smooth"
                      style={{ WebkitOverflowScrolling: "touch" }}
                      data-testid="admin-tabs-scroller"
                    >
                      <TabsList className="bg-[#F0ECF7] inline-flex flex-nowrap gap-1 h-auto p-1.5 rounded-xl w-max">
                        {tabs.map((t) => (
                          <TabsTrigger
                            key={t.value}
                            value={t.value}
                            className="data-[state=active]:bg-white shrink-0 text-xs px-3 py-1.5 rounded-lg"
                            data-testid={t.testid}
                          >
                            {t.value === "reports" ? (
                              <span className="inline-flex items-center gap-1.5">
                                {t.label}
                                {t.count > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-semibold inline-flex items-center justify-center" data-testid="reports-badge">{t.count}</span>}
                              </span>
                            ) : (
                              <>{t.label}{t.count !== null && t.count !== undefined ? ` (${t.count})` : ""}</>
                            )}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </div>
                  </>
                );
              })()}
            </div>

            <TabsContent value="pending">
              {pendingRequests.length > 0 ? (
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div
                      key={request.verification_id}
                      className="border border-[#E5DFEC] rounded-xl p-4"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center font-medium">
                            {request.user_name?.[0] || 'U'}
                          </div>
                          <div>
                            <h4 className="font-medium text-[#6B4E8A]">{request.user_name}</h4>
                            <p className="text-sm text-[#64748B]">{request.user_email}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className="px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full">
                                {request.province}
                              </span>
                              <span className="text-xs text-[#64748B]">
                                Reg: {request.registration_number}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isFounder && !request.files_deleted ? (
                            <>
                              {request.has_id_doc && <SecureDocButton kind="id" verificationId={request.verification_id} label="ID" testId={`ver-doc-id-${request.verification_id}`} />}
                              {request.has_permit_doc && <SecureDocButton kind="permit" verificationId={request.verification_id} label="Permit" testId={`ver-doc-permit-${request.verification_id}`} />}
                            </>
                          ) : (
                            <DocStatusNote deleted={request.files_deleted} testId={`ver-doc-note-${request.verification_id}`} />
                          )}
                          <Button
                            onClick={() => handleApprove(request.verification_id)}
                            className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full"
                            disabled={processingId === request.verification_id}
                            data-testid={`approve-${request.verification_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReject(request.verification_id)}
                            variant="outline"
                            className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 rounded-full"
                            disabled={processingId === request.verification_id}
                            data-testid={`reject-${request.verification_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-[#64748B] mt-3">
                        Submitted: {formatDate(request.submitted_at)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No pending verification requests</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="anonymous">
              {anonymousPosts.length > 0 ? (
                <div className="space-y-4">
                  {anonymousPosts.map((post) => (
                    <div
                      key={post.discussion_id}
                      className="border border-[#E5DFEC] rounded-xl p-4"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#64748B] text-white flex items-center justify-center">
                            <EyeOff className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-[#6B4E8A]">{post.title}</h4>
                            <p className="text-sm text-[#64748B] mt-1 line-clamp-3">{post.content}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {post.tags?.map((tag, i) => (
                                <span key={i} className="px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <p className="text-xs text-[#64748B] mt-2">
                              Posted: {formatDate(post.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            onClick={() => handleApprovePost(post.discussion_id)}
                            className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full"
                            disabled={processingId === post.discussion_id}
                            data-testid={`approve-post-${post.discussion_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectPost(post.discussion_id)}
                            variant="outline"
                            className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 rounded-full"
                            disabled={processingId === post.discussion_id}
                            data-testid={`reject-post-${post.discussion_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No pending anonymous posts</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="alerts">
              {notifications.length > 0 ? (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div
                      key={notif.notification_id}
                      className={`border rounded-xl p-4 ${notif.is_read ? 'border-[#E5DFEC] bg-white' : 'border-[#8DB5A0] bg-[#8DB5A0]/5'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            notif.type === 'content_flagged' || notif.type === 'reply_flagged' ? 'bg-[#8DB5A0]/10' : 'bg-[#64748B]/10'
                          }`}>
                            {notif.type === 'content_flagged' || notif.type === 'reply_flagged' ? <AlertTriangle className="w-5 h-5 text-[#8DB5A0]" /> :
                             <Bell className="w-5 h-5 text-[#64748B]" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[#6B4E8A]">
                              {notif.type === 'content_flagged' ? 'Content Flagged' :
                               notif.type === 'reply_flagged' ? 'Reply Flagged' : 'Notification'}
                            </p>
                            <p className="text-sm text-[#64748B] mt-1">{notif.message}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-xs text-[#64748B]">{formatDate(notif.created_at)}</span>
                              {notif.user_name && (
                                <span className="text-xs text-[#6B4E8A] bg-[#F0ECF7] px-2 py-0.5 rounded-full">
                                  {notif.user_name}
                                </span>
                              )}
                              {notif.severity && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  notif.severity === 'critical' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                                  notif.severity === 'high' ? 'bg-[#8DB5A0]/10 text-[#8DB5A0]' :
                                  'bg-[#8DB5A0]/10 text-[#8DB5A0]'
                                }`}>
                                  {notif.severity}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {!notif.is_read && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMarkNotifRead(notif.notification_id)}
                              className="text-xs text-[#64748B]"
                            >
                              Dismiss
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No security alerts</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="clinic-reviews">
              {clinicReviews.length > 0 ? (
                <div className="space-y-4">
                  {clinicReviews.map((review) => (
                    <div key={review.review_id} className="border border-[#E5DFEC] rounded-xl p-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-medium text-[#6B4E8A]">{review.clinic_name}</h4>
                            <div className="flex gap-0.5">
                              {[1,2,3,4,5].map(i => (
                                <Star key={i} className={`w-4 h-4 ${i <= review.overall_rating ? 'text-[#8DB5A0] fill-[#8DB5A0]' : 'text-[#E5DFEC]'}`} />
                              ))}
                            </div>
                            {review.is_flagged && (
                              <span className="px-2 py-0.5 text-xs bg-[#EF4444]/10 text-[#EF4444] rounded-full">Flagged</span>
                            )}
                          </div>
                          <p className="text-xs text-[#64748B]">{review.clinic_location}, {review.clinic_province}</p>
                          <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            <span>Breaks: <strong>{review.breaks_provided}</strong></span>
                            <span>Cancel pay: <strong>{review.cancellation_pay}</strong></span>
                            <span>Pay on time: <strong>{review.payments_on_time}</strong></span>
                            <span>Instruments: <strong>{review.sufficient_instruments}</strong></span>
                            <span>Sterilization: <strong>{review.sterilization_protocols}</strong></span>
                            <span>PPE: <strong>{review.adequate_ppe}</strong></span>
                          </div>
                          {review.pros && <p className="text-sm text-[#10B981] mt-2">Pros: {review.pros}</p>}
                          {review.cons && <p className="text-sm text-[#EF4444] mt-1">Cons: {review.cons}</p>}
                          {review.flag_reason && <p className="text-xs text-[#8DB5A0] mt-1 bg-[#8DB5A0]/10 p-2 rounded">Flag: {review.flag_reason}</p>}
                          <p className="text-xs text-[#64748B] mt-2">{formatDate(review.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button onClick={() => handleApproveReview(review.review_id)} className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" disabled={processingId === review.review_id} data-testid={`approve-review-${review.review_id}`}>
                            <Eye className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button onClick={() => handleRejectReview(review.review_id)} variant="outline" className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 rounded-full" disabled={processingId === review.review_id}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Star className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No pending clinic reviews</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="partners-pending">
              {partnerApps.length > 0 ? (
                <div className="space-y-4">
                  {partnerApps.map((app) => (
                    <div key={app.partner_id} className="border border-[#E5DFEC] rounded-xl p-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="font-medium text-[#6B4E8A]">{app.business_name}</h4>
                          <p className="text-sm text-[#64748B]">{app.contact_name} &middot; {app.email}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs px-2 py-0.5 bg-[#8DB5A0]/10 text-[#8DB5A0] rounded-full">{app.specialty}</span>
                            <span className="text-xs px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] rounded-full">{app.location}, {app.province}</span>
                          </div>
                          <p className="text-sm text-[#64748B] mt-2 line-clamp-3">{app.description}</p>
                          {app.member_discount && <p className="text-xs text-[#10B981] mt-1">Discount: {app.member_discount}</p>}
                          {app.website && <p className="text-xs text-[#3B82F6] mt-1">{app.website}</p>}
                          <p className="text-xs text-[#64748B] mt-2">{formatDate(app.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button onClick={() => handleApprovePartner(app.partner_id)} className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" disabled={processingId === app.partner_id} data-testid={`approve-partner-${app.partner_id}`}>
                            <Eye className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button onClick={() => handleRejectPartner(app.partner_id)} variant="outline" className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 rounded-full" disabled={processingId === app.partner_id}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Handshake className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No pending partner applications</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="events-pending" data-testid="events-pending-content">
              {pendingEvents.length > 0 ? (
                <div className="space-y-4">
                  {pendingEvents.map((evt) => (
                    <div key={evt.event_id} className="border border-[#E5DFEC] rounded-xl p-4" data-testid={`pending-event-${evt.event_id}`}>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-medium text-[#6B4E8A] truncate">{evt.title}</h4>
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] rounded-full">{evt.event_type}</span>
                            {evt.ai_verified === false && (
                              <span className="text-[10px] uppercase px-2 py-0.5 bg-[#FEE2E2] text-[#B91C1C] rounded-full">AI: not verified</span>
                            )}
                            {evt.evidence_fetched && (
                              <span className="text-[10px] uppercase px-2 py-0.5 bg-[#DCFCE7] text-[#15803D] rounded-full">URL fetched</span>
                            )}
                          </div>
                          <p className="text-sm text-[#64748B] mt-1 line-clamp-3">{evt.description}</p>
                          <div className="flex flex-wrap gap-2 mt-2 text-xs text-[#64748B]">
                            <span>By: {evt.created_by_name}</span>
                            <span>&middot;</span>
                            <span>Starts: {formatDate(evt.start_date)}</span>
                            {evt.organizer && <><span>&middot;</span><span>Organizer: {evt.organizer}</span></>}
                            {evt.location && <><span>&middot;</span><span>{evt.location}</span></>}
                          </div>
                          {evt.url && (
                            <a href={safeHref(evt.url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#3B82F6] mt-2 hover:underline">
                              <ExternalLink className="w-3 h-3" /> {evt.url}
                            </a>
                          )}
                          {evt.ai_notes && (
                            <p className="text-xs text-[#64748B] italic mt-2 bg-[#F0ECF7]/50 border border-[#E5DFEC] rounded-md p-2">
                              AI: {evt.ai_notes}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button onClick={() => handleApproveEvent(evt.event_id)} className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" disabled={processingId === evt.event_id} data-testid={`approve-event-${evt.event_id}`}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button onClick={() => handleRejectEvent(evt.event_id)} variant="outline" className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 rounded-full" disabled={processingId === evt.event_id} data-testid={`reject-event-${evt.event_id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No events awaiting review. AI verification approves legitimate events automatically.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="partner-interest" data-testid="partner-interest-content">
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-medium text-[#0F172A]" style={{ fontFamily: "Outfit, sans-serif" }}>Partner Applications</h3>
                    <p className="text-xs text-[#64748B]">Submissions from the "Partner with DHDH" form on the prelaunch landing. Each one was also forwarded to your inbox.</p>
                  </div>
                  <div className="inline-flex bg-[#F0ECF7] border border-[#E5DFEC] rounded-full p-1" data-testid="partner-filter-toggle">
                    {[
                      { v: "new", label: `New (${partnerInterest.filter(p => p.status !== "replied").length})` },
                      { v: "replied", label: `Replied (${partnerInterest.filter(p => p.status === "replied").length})` },
                      { v: "all", label: `All (${partnerInterest.length})` },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setPartnerFilter(opt.v)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${partnerFilter === opt.v ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"}`}
                        data-testid={`partner-filter-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {(() => {
                  const filtered = partnerInterest.filter(p =>
                    partnerFilter === "all"
                      ? true
                      : partnerFilter === "replied"
                        ? p.status === "replied"
                        : p.status !== "replied"
                  );
                  if (filtered.length === 0) {
                    return (
                      <div className="bg-[#FAF7F2] rounded-2xl p-8 text-center border border-[#E5DFEC]" data-testid="partner-empty">
                        <Handshake className="w-10 h-10 text-[#94A3B8] mx-auto mb-2" />
                        <p className="text-[#64748B]">No {partnerFilter === "all" ? "" : partnerFilter} partner applications yet.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2" data-testid="partner-list">
                      {filtered.map((row) => {
                        const expanded = expandedInterest === row.interest_id;
                        const firstName = (row.full_name || "").split(" ")[0] || "there";
                        return (
                          <ExpandableSubmissionCard
                            key={row.interest_id}
                            testId={`partner-interest-${row.interest_id}`}
                            expanded={expanded}
                            onToggle={() => setExpandedInterest(expanded ? null : row.interest_id)}
                            summary={
                              <>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-medium text-[#0F172A] truncate">{row.full_name}</p>
                                  <span className="text-xs text-[#64748B]">·</span>
                                  <p className="text-sm font-medium text-[#6B4E8A] truncate">{row.company_name}</p>
                                  <SubmissionStatusBadge status={row.status} />
                                  <SubmissionSentBadge emailed={row.emailed} />
                                </div>
                                <p className="text-xs text-[#64748B] truncate mt-0.5">{row.email}</p>
                                <p className="text-xs text-[#94A3B8] mt-0.5">{formatDate(row.created_at)}</p>
                                {!expanded && <p className="text-sm text-[#475569] mt-1.5 line-clamp-1">{row.offering}</p>}
                              </>
                            }
                            details={
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="text-xs uppercase tracking-wide text-[#94A3B8] block mb-0.5">Services / offering</span>
                                  <p className="text-[#2A2A2A] whitespace-pre-wrap leading-relaxed">{row.offering}</p>
                                </div>
                                {row.provinces_served && row.provinces_served.length > 0 && (
                                  <div>
                                    <span className="text-xs uppercase tracking-wide text-[#94A3B8] block mb-1">Provinces served</span>
                                    <div className="flex flex-wrap items-center gap-1" data-testid={`partner-interest-provinces-${row.interest_id}`}>
                                      <MapPin className="w-3 h-3 text-[#6B4E8A]" />
                                      {row.provinces_served.length === 13 ? (
                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#6B4E8A]/10 text-[#6B4E8A]">All of Canada</span>
                                      ) : (
                                        row.provinces_served.map((code) => (
                                          <span key={code} className="text-[11px] px-2 py-0.5 rounded-full bg-[#8DB5A0]/15 text-[#8DB5A0] border border-[#8DB5A0]/20">{code}</span>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                )}
                                {row.referral_source && (
                                  <div>
                                    <span className="text-xs uppercase tracking-wide text-[#94A3B8] block mb-0.5">Heard about DHDH via</span>
                                    <p className="text-[#475569]">{row.referral_source}</p>
                                  </div>
                                )}
                              </div>
                            }
                            actions={
                              <>
                                <a
                                  href={`mailto:${row.email}?subject=${encodeURIComponent(`Re: Partnering with DHDH — ${row.company_name}`)}&body=${encodeURIComponent(`Hi ${firstName},\n\nThank you for your interest in partnering with DHDH.\n\n\n\n— Original submission from ${row.company_name} —\n${row.offering}`)}`}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full text-xs font-medium"
                                  data-testid={`partner-reply-via-email-${row.interest_id}`}
                                >
                                  <Mail className="w-3 h-3" /> Reply via email
                                </a>
                                {row.status !== "replied" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleMarkInterestReplied(row.interest_id)}
                                    disabled={processingId === row.interest_id}
                                    className="rounded-full text-xs h-8"
                                    data-testid={`partner-interest-replied-${row.interest_id}`}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Mark as replied
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteInterest(row.interest_id)}
                                  disabled={processingId === row.interest_id}
                                  className="text-[#EF4444] hover:bg-[#FEE2E2] rounded-full text-xs h-8 ml-auto"
                                  data-testid={`partner-interest-delete-${row.interest_id}`}
                                >
                                  <XCircle className="w-3 h-3 mr-1" /> Delete
                                </Button>
                              </>
                            }
                          />
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </TabsContent>

            <TabsContent value="student-eligibility">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>Student & New Grad Eligibility</h3>
                  <p className="text-xs text-[#64748B]">One-click approve to unlock the $5/$45 Student tier for the member.</p>
                </div>
                <div className="inline-flex bg-[#F0ECF7] border border-[#E5DFEC] rounded-full p-1" data-testid="elig-filter-toggle">
                  {[
                    { v: "submitted", label: "Pending" },
                    { v: "approved", label: "Approved" },
                    { v: "rejected", label: "Rejected" },
                    { v: "all", label: "All" },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => { setEligibilityFilter(opt.v); refreshEligibility(opt.v); }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${eligibilityFilter === opt.v ? "bg-white text-[#6B4E8A] shadow-sm" : "text-[#64748B] hover:text-[#6B4E8A]"}`}
                      data-testid={`elig-filter-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {studentEligibility.length === 0 ? (
                <div className="bg-[#FAF7F2] rounded-2xl p-8 text-center border border-[#E5DFEC]" data-testid="elig-empty">
                  <GraduationCap className="w-10 h-10 text-[#94A3B8] mx-auto mb-2" />
                  <p className="text-[#64748B]">No {eligibilityFilter === "submitted" ? "pending" : eligibilityFilter} eligibility submissions</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="elig-list">
                  {studentEligibility.map((u) => {
                    const isPending = u.student_eligibility_status === "submitted";
                    const submittedDate = u.student_eligibility_submitted_at ? new Date(u.student_eligibility_submitted_at).toLocaleDateString("en-CA") : "—";
                    return (
                      <div key={u.user_id} className="border border-[#E5DFEC] rounded-2xl bg-white overflow-hidden" data-testid={`elig-row-${u.user_id}`}>
                        <div className="p-4 flex items-start gap-4 flex-wrap">
                          <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center shrink-0">
                            <GraduationCap className="w-5 h-5 text-[#8B5CF6]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-[#6B4E8A] truncate">{u.name || "(no name)"}</p>
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                u.student_eligibility_status === "approved" ? "bg-[#10B981]/15 text-[#059669]" :
                                u.student_eligibility_status === "rejected" ? "bg-[#EF4444]/15 text-[#DC2626]" :
                                "bg-[#FBBF24]/20 text-[#92400E]"
                              }`}>
                                {u.student_eligibility_status}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#64748B]">
                                {u.student_eligibility_type === "new_grad" ? `New Grad ${u.student_eligibility_grad_year || ""}` : "Student"}
                              </span>
                            </div>
                            <p className="text-xs text-[#64748B] truncate">{u.email}</p>
                            <p className="text-[11px] text-[#94A3B8] mt-1">
                              {u.province || "—"} · Submitted {submittedDate}
                              {u.student_eligibility_notes ? ` · "${u.student_eligibility_notes}"` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {isFounder && u.has_document ? (
                              <SecureDocButton kind="eligibility" userId={u.user_id} label="View document" testId={`elig-preview-${u.user_id}-btn`} />
                            ) : (
                              <DocStatusNote deleted={!u.has_document} testId={`elig-doc-note-${u.user_id}`} />
                            )}
                            {isPending && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-[#10B981] hover:bg-[#059669] text-white"
                                  onClick={() => handleApproveEligibility(u.user_id)}
                                  disabled={processingId === u.user_id}
                                  data-testid={`elig-approve-${u.user_id}-btn`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-[#EF4444] text-[#DC2626] hover:bg-[#FEF2F2]"
                                  onClick={() => {
                                    const notes = window.prompt("Reason for rejection (optional, will be visible to the member):") || "";
                                    handleRejectEligibility(u.user_id, notes);
                                  }}
                                  disabled={processingId === u.user_id}
                                  data-testid={`elig-reject-${u.user_id}-btn`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Beta Invites Tab */}
            <TabsContent value="beta-invites" data-testid="beta-invites-content">
              <div className="space-y-6">
                {/* Generator card */}
                <form
                  id="beta-invite-form"
                  onSubmit={handleCreateInvite}
                  className="bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] rounded-2xl p-5 text-white"
                  data-testid="beta-invite-generator"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#8DB5A0]/20 flex items-center justify-center shrink-0">
                      <Ticket className="w-5 h-5 text-[#8DB5A0]" />
                    </div>
                    <div>
                      <h3 className="text-base font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Generate Beta Invite</h3>
                      <p className="text-xs text-white/60 mt-0.5">Each code is a unique 6-character single-use code. Beta testers automatically lock in the Founding RDH Pro rate for life.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-white/70">Name</Label>
                      <Input
                        value={inviteForm.invitee_name}
                        onChange={(e) => setInviteForm({ ...inviteForm, invitee_name: e.target.value })}
                        placeholder="Sarah Smith"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 mt-1"
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/70">Email</Label>
                      <Input
                        type="email"
                        value={inviteForm.invitee_email}
                        onChange={(e) => setInviteForm({ ...inviteForm, invitee_email: e.target.value })}
                        placeholder="sarah@example.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 mt-1"
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/70">Note (optional)</Label>
                      <Input
                        value={inviteForm.note}
                        onChange={(e) => setInviteForm({ ...inviteForm, note: e.target.value })}
                        placeholder="From conference"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 mt-1"
                        data-testid="invite-note-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={creatingInvite}
                    className="mt-4 bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#7AA38F] rounded-full font-medium"
                    data-testid="create-invite-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {creatingInvite ? "Generating…" : "Generate Code"}
                  </Button>
                </form>

                {/* Filter pills */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="inline-flex bg-[#F0ECF7] border border-[#E5DFEC] rounded-full p-1" data-testid="invite-filter-toggle">
                    {[
                      { v: "available", label: `Available (${betaInvites.filter(i => !i.used && !i.revoked).length})` },
                      { v: "used", label: `Used (${betaInvites.filter(i => i.used).length})` },
                      { v: "revoked", label: `Revoked (${betaInvites.filter(i => i.revoked).length})` },
                      { v: "all", label: `All (${betaInvites.length})` },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setInviteFilter(opt.v)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${inviteFilter === opt.v ? "bg-white text-[#6B4E8A] shadow-sm" : "text-[#64748B] hover:text-[#6B4E8A]"}`}
                        data-testid={`invite-filter-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => refreshInvites()} className="rounded-full text-xs" data-testid="refresh-invites-btn">
                    Refresh
                  </Button>
                </div>

                {/* Invite list */}
                {filteredInvites.length === 0 ? (
                  <div className="bg-[#FAF7F2] rounded-2xl p-8 text-center border border-[#E5DFEC]" data-testid="invites-empty">
                    <Ticket className="w-10 h-10 text-[#94A3B8] mx-auto mb-2" />
                    <p className="text-[#64748B]">
                      No {inviteFilter === "all" ? "" : inviteFilter} beta invites yet. Generate one above to start onboarding testers.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="invites-list">
                    {filteredInvites.map((inv) => {
                      const status = inv.used ? "used" : inv.revoked ? "revoked" : "available";
                      const statusColor = status === "available" ? "#10B981" : status === "used" ? "#64748B" : "#EF4444";
                      return (
                        <div
                          key={inv.code}
                          className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-[#E5DFEC] rounded-xl bg-white"
                          data-testid={`invite-row-${inv.code}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <code className="px-3 py-1.5 bg-[#F0ECF7] rounded-lg text-sm font-mono font-semibold text-[#6B4E8A] shrink-0">
                              {inv.code}
                            </code>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#6B4E8A] truncate">{inv.invitee_name}</p>
                              <p className="text-xs text-[#64748B] truncate">{inv.invitee_email}</p>
                              {inv.note && <p className="text-xs text-[#94A3B8] italic mt-0.5 truncate">"{inv.note}"</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                              style={{ backgroundColor: `${statusColor}1A`, color: statusColor }}
                            >
                              {status}
                            </span>
                            <span className="text-xs text-[#94A3B8]">{formatDate(inv.created_at)}</span>
                            {status === "available" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCopyCode(inv.code)}
                                  className="rounded-full text-xs h-8"
                                  data-testid={`copy-code-${inv.code}`}
                                >
                                  <Copy className="w-3 h-3 mr-1" /> Copy
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRevokeInvite(inv.code)}
                                  disabled={processingId === inv.code}
                                  className="rounded-full text-xs h-8 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                                  data-testid={`revoke-code-${inv.code}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" /> Revoke
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Waitlist Tab */}
            <TabsContent value="waitlist" data-testid="waitlist-content">
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>Prelaunch Waitlist</h3>
                    <p className="text-xs text-[#64748B]">{waitlist.length} {waitlist.length === 1 ? "person has" : "people have"} signed up to be notified at launch.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                      <Input
                        placeholder="Search waitlist…"
                        value={waitlistSearch}
                        onChange={(e) => setWaitlistSearch(e.target.value)}
                        className="pl-9 rounded-full text-sm h-9 w-56"
                        data-testid="waitlist-search-input"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={refreshWaitlist} className="rounded-full text-xs" data-testid="refresh-waitlist-btn">
                      Refresh
                    </Button>
                  </div>
                </div>

                {filteredWaitlist.length === 0 ? (
                  <div className="bg-[#FAF7F2] rounded-2xl p-8 text-center border border-[#E5DFEC]" data-testid="waitlist-empty">
                    <Mail className="w-10 h-10 text-[#94A3B8] mx-auto mb-2" />
                    <p className="text-[#64748B]">{waitlistSearch ? "No matches for your search" : "No waitlist signups yet"}</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="waitlist-list">
                    {filteredWaitlist.map((row) => (
                      <div
                        key={row.waitlist_id || row.email}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-[#E5DFEC] rounded-xl bg-white"
                        data-testid={`waitlist-row-${row.email}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#6B4E8A] truncate">{row.name || "—"}</p>
                          <p className="text-xs text-[#64748B] truncate">{row.email}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-[#94A3B8]">
                            {row.province && <span className="px-1.5 py-0.5 bg-[#F0ECF7] rounded">{row.province}</span>}
                            {row.referral_source && <span>via {row.referral_source}</span>}
                            <span>· {formatDate(row.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleInviteFromWaitlist(row)}
                            className="rounded-full text-xs h-8 bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
                            data-testid={`invite-from-waitlist-${row.email}`}
                          >
                            <Send className="w-3 h-3 mr-1" /> Send Invite
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRemoveFromWaitlist(row.email)}
                            disabled={processingId === row.email}
                            className="rounded-full text-xs h-8 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                            data-testid={`remove-waitlist-${row.email}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Contact Questions Tab */}
            <TabsContent value="contact-questions" data-testid="contact-questions-content">
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-medium text-[#0F172A]" style={{ fontFamily: "Outfit, sans-serif" }}>Public Contact Questions</h3>
                    <p className="text-xs text-[#64748B]">Submissions from the "Have a question?" form on the prelaunch landing. Each one was also forwarded to your inbox.</p>
                  </div>
                  <div className="inline-flex bg-[#F0ECF7] border border-[#E5DFEC] rounded-full p-1" data-testid="questions-filter-toggle">
                    {[
                      { v: "new", label: `New (${contactQuestions.filter(q => q.status === "new").length})` },
                      { v: "replied", label: `Replied (${contactQuestions.filter(q => q.status === "replied").length})` },
                      { v: "all", label: `All (${contactQuestions.length})` },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setQuestionsFilter(opt.v)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${questionsFilter === opt.v ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"}`}
                        data-testid={`questions-filter-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {filteredQuestions.length === 0 ? (
                  <div className="bg-[#FAF7F2] rounded-2xl p-8 text-center border border-[#E5DFEC]" data-testid="questions-empty">
                    <MessageSquare className="w-10 h-10 text-[#94A3B8] mx-auto mb-2" />
                    <p className="text-[#64748B]">No {questionsFilter === "all" ? "" : questionsFilter} questions yet.</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="questions-list">
                    {filteredQuestions.map((q) => {
                      const expanded = expandedQuestion === q.question_id;
                      const firstName = q.name?.split(" ")[0] || "there";
                      return (
                        <ExpandableSubmissionCard
                          key={q.question_id}
                          testId={`question-row-${q.question_id}`}
                          expanded={expanded}
                          onToggle={() => setExpandedQuestion(expanded ? null : q.question_id)}
                          summary={
                            <>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-[#0F172A] truncate">{q.name}</p>
                                <SubmissionStatusBadge status={q.status} />
                                <SubmissionSentBadge emailed={q.emailed} />
                              </div>
                              <p className="text-xs text-[#64748B] truncate mt-0.5">{q.email}</p>
                              <p className="text-xs text-[#94A3B8] mt-0.5">{formatDate(q.created_at)}</p>
                              {!expanded && <p className="text-sm text-[#475569] mt-1.5 line-clamp-1">{q.message}</p>}
                            </>
                          }
                          details={
                            <p className="text-sm text-[#2A2A2A] whitespace-pre-wrap leading-relaxed mb-1">{q.message}</p>
                          }
                          actions={
                            <>
                              <a
                                href={`mailto:${q.email}?subject=${encodeURIComponent('Re: your question to DHDH')}&body=${encodeURIComponent(`Hi ${firstName},\n\nThanks for reaching out about DHDH.\n\n\n\n— Original message —\n${q.message}`)}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full text-xs font-medium"
                                data-testid={`reply-via-email-${q.question_id}`}
                              >
                                <Mail className="w-3 h-3" /> Reply via email
                              </a>
                              {q.status !== "replied" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkReplied(q.question_id)}
                                  disabled={processingId === q.question_id}
                                  className="rounded-full text-xs h-8"
                                  data-testid={`mark-replied-${q.question_id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" /> Mark as replied
                                </Button>
                              )}
                            </>
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {isFounder && (
            <TabsContent value="guest-pros" data-testid="guest-pros-content">
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-medium text-[#0F172A]" style={{ fontFamily: "Outfit, sans-serif" }}>Guest Professionals</h3>
                    <p className="text-xs text-[#64748B] max-w-xl">
                      Founder-only. Manually created accounts for lawyers, accountants, and other consultants who need read-only access without RDH verification. These accounts are invisible to members and excluded from every notification/digest pipeline.
                    </p>
                  </div>
                  <Button
                    onClick={() => { setLastCreatedGuestPro(null); setShowGuestProModal(true); }}
                    className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full"
                    data-testid="create-guest-pro-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Create Guest Professional Account
                  </Button>
                </div>

                {guestPros.length === 0 ? (
                  <div className="bg-[#FAF7F2] rounded-2xl p-8 text-center border border-[#E5DFEC]" data-testid="guest-pros-empty">
                    <Crown className="w-10 h-10 text-[#94A3B8] mx-auto mb-2" />
                    <p className="text-[#64748B]">No Guest Professional accounts yet.</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="guest-pros-list">
                    {guestPros.map((g) => (
                      <div
                        key={g.user_id}
                        className={`border rounded-xl p-4 ${g.guest_revoked ? "border-[#FEE2E2] bg-[#FFF7F7]" : "border-[#E5DFEC] bg-white"}`}
                        data-testid={`guest-pro-row-${g.user_id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-[#0F172A] truncate">{g.name}</p>
                              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-medium bg-[#8DB5A0]/15 text-[#8DB5A0] border border-[#8DB5A0]/30">Guest Professional</span>
                              {g.guest_revoked && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-medium bg-[#FEE2E2] text-[#EF4444]">Revoked</span>
                              )}
                            </div>
                            <p className="text-xs text-[#64748B] truncate mt-0.5">{g.email}</p>
                            <p className="text-sm text-[#475569] mt-1.5">
                              <span className="font-medium text-[#6B4E8A]">{g.company_or_firm}</span>
                              {g.reason && <> · {g.reason}</>}
                            </p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#94A3B8]">
                              {g.access_start_at && <span>Starts {g.access_start_at.slice(0,10)}</span>}
                              {g.access_end_at ? <span>Expires {g.access_end_at.slice(0,10)}</span> : <span>No expiry</span>}
                              <span>Created {formatDate(g.created_at)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {g.guest_revoked ? (
                              <Button size="sm" onClick={() => handleReinstateGuestPro(g.user_id)} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full text-xs" data-testid={`reinstate-guest-${g.user_id}`}>
                                Reinstate
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => handleRevokeGuestPro(g.user_id, g.name)} className="rounded-full text-xs" data-testid={`revoke-guest-${g.user_id}`}>
                                Revoke
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteGuestPro(g.user_id, g.name)} className="text-[#EF4444] hover:bg-[#FEE2E2] rounded-full text-xs" data-testid={`delete-guest-${g.user_id}`}>
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
            )}

            <TabsContent value="audit">
              {auditLogs.length > 0 ? (
                <div className="space-y-2">
                  {auditLogs.map((log) => (
                    <div key={log.audit_id} className="flex items-start gap-3 p-3 border border-[#E5DFEC] rounded-xl">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        log.action.includes('approve') ? 'bg-[#10B981]/10' :
                        log.action.includes('reject') || log.action.includes('ban') ? 'bg-[#EF4444]/10' :
                        log.action.includes('role') ? 'bg-[#8DB5A0]/10' :
                        'bg-[#64748B]/10'
                      }`}>
                        {log.action.includes('approve') ? <CheckCircle className="w-4 h-4 text-[#10B981]" /> :
                         log.action.includes('reject') ? <XCircle className="w-4 h-4 text-[#EF4444]" /> :
                         log.action.includes('role') ? <UserCog className="w-4 h-4 text-[#8DB5A0]" /> :
                         log.action.includes('password') ? <KeyRound className="w-4 h-4 text-[#8DB5A0]" /> :
                         log.action.includes('unban') ? <Shield className="w-4 h-4 text-[#10B981]" /> :
                         <FileText className="w-4 h-4 text-[#64748B]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#6B4E8A]">
                          <strong>{log.actor_name}</strong>
                          <span className="text-[#64748B]"> {log.action.replace(/_/g, ' ')} </span>
                          <span className="text-[#6B4E8A]">{log.target_type}</span>
                        </p>
                        {log.details && <p className="text-xs text-[#64748B] mt-0.5 truncate">{log.details}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-[#64748B]">{formatDate(log.created_at)}</span>
                          {log.actor_role && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                              log.actor_role === 'founder' ? 'bg-[#8DB5A0]/10 text-[#8DB5A0]' :
                              log.actor_role === 'admin' ? 'bg-[#6B4E8A]/10 text-[#6B4E8A]' :
                              'bg-[#8DB5A0]/10 text-[#8DB5A0]'
                            }`}>
                              {log.actor_role}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No audit log entries yet</p>
                  <p className="text-xs text-[#64748B] mt-1">Actions like approvals, rejections, role changes, and password resets will appear here</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="processed">
              {processedRequests.length > 0 ? (
                <div className="space-y-3">
                  {processedRequests.map((request) => (
                    <div
                      key={request.verification_id}
                      className="flex items-center justify-between p-4 border border-[#E5DFEC] rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          request.status === "approved" ? "bg-[#10B981]" : "bg-[#EF4444]"
                        } text-white`}>
                          {request.status === "approved" ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#6B4E8A]">{request.user_name}</p>
                          <p className="text-xs text-[#64748B]">
                            {request.province} • {request.registration_number}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          request.status === "approved" 
                            ? "bg-[#10B981]/10 text-[#10B981]" 
                            : "bg-[#EF4444]/10 text-[#EF4444]"
                        }`}>
                          {request.status === "approved" ? "Approved" : "Rejected"}
                        </span>
                        <p className="text-xs text-[#64748B] mt-1">
                          {request.reviewed_at && formatDate(request.reviewed_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <p className="text-[#64748B]">No processed requests yet</p>
                </div>
              )}
            </TabsContent>

            {/* Users Management Tab — visible to all admins; founder-only Admin toggle is gated inside */}
            <TabsContent value="users">
              {isFounder && <AdminEmailMigrationCard />}
              {isFounder && <AccountPurgeReportCard />}
              {isFounder && <BackupsCard />}
              <div className="mb-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className="pl-10 bg-[#FAF7F2] border-[#E5DFEC]"
                      data-testid="user-search-input"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {filteredUsers.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-4 border border-[#E5DFEC] rounded-xl"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium shrink-0 ${
                          u.admin_role === 'founder' ? 'bg-[#8DB5A0]' :
                          u.admin_role === 'admin' ? 'bg-[#6B4E8A]' :
                          u.admin_role === 'moderator' ? 'bg-[#8DB5A0]' :
                          'bg-[#64748B]'
                        }`}>
                          {u.admin_role === 'founder' ? <Crown className="w-5 h-5" /> : u.name?.[0] || 'U'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#6B4E8A] truncate">{u.name || 'Unnamed'}</p>
                            {u.admin_role ? (
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                u.admin_role === 'founder' ? 'bg-[#8DB5A0]/20 text-[#8DB5A0]' :
                                u.admin_role === 'admin' ? 'bg-[#6B4E8A]/10 text-[#6B4E8A]' :
                                'bg-[#8DB5A0]/10 text-[#8DB5A0]'
                              }`} data-testid={`role-badge-${u.user_id}`}>
                                {u.admin_role}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-[#64748B]/10 text-[#64748B]" data-testid={`role-badge-${u.user_id}`}>
                                member
                              </span>
                            )}
                            {u.is_banned && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-[#EF4444]/10 text-[#EF4444] font-medium">banned</span>
                            )}
                            {u.is_beta_tester && !u.lost_beta_lock && (
                              <span
                                className="px-2 py-0.5 text-xs rounded-full bg-[#6B4E8A] text-white font-medium"
                                data-testid={`beta-founding-badge-${u.user_id}`}
                                title="Beta Founding Member — $19/mo locked for life"
                              >
                                Beta Founding
                              </span>
                            )}
                            {u.lost_beta_lock && (
                              <span
                                className="px-2 py-0.5 text-xs rounded-full bg-[#F59E0B]/10 text-[#D97706] font-medium"
                                title="Lost beta rate after subscription lapsed"
                              >
                                Beta · lapsed
                              </span>
                            )}
                            {/* Phase 64.F member_type badges */}
                            {u.is_lifetime_free && (
                              <span
                                className="px-2 py-0.5 text-xs rounded-full bg-gradient-to-r from-[#D89B4E] to-[#B8842F] text-white font-medium"
                                data-testid={`lifetime-badge-${u.user_id}`}
                                title="Lifetime-free hard guard account"
                              >
                                Lifetime
                              </span>
                            )}
                            {u.is_founding_member && !u.is_lifetime_free && (
                              <span
                                className="px-2 py-0.5 text-xs rounded-full bg-[#8DB5A0] text-white font-medium"
                                data-testid={`founding-badge-${u.user_id}`}
                                title="FOUNDING100 member — locked for life"
                              >
                                Founding
                              </span>
                            )}
                            {(u.member_type && !u.is_lifetime_free && !u.is_founding_member && !u.is_beta_tester) && (
                              <span
                                className="px-2 py-0.5 text-xs rounded-full bg-[#64748B]/10 text-[#64748B] font-medium"
                                data-testid={`type-badge-${u.user_id}`}
                              >
                                {u.member_type}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#64748B] truncate">{u.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              u.verification_status === 'verified' ? 'bg-[#10B981]/10 text-[#10B981]' :
                              u.verification_status === 'submitted' ? 'bg-[#8DB5A0]/10 text-[#8DB5A0]' :
                              'bg-[#64748B]/10 text-[#64748B]'
                            }`}>
                              {u.verification_status || 'pending'}
                            </span>
                            {u.province && <span className="text-xs text-[#64748B]">{u.province}</span>}
                            {/* Phase 64.F: trial + locked-rate metadata */}
                            {u.membership_status && (
                              <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider" data-testid={`status-${u.user_id}`}>
                                {u.membership_status}
                              </span>
                            )}
                            {u.trial_days_remaining !== null && u.trial_days_remaining !== undefined && (
                              <span className="text-[10px] text-[#94A3B8]" data-testid={`trial-${u.user_id}`}>
                                {u.trial_days_remaining}d left
                              </span>
                            )}
                            {u.plan_tier && u.membership_status !== 'pending_approval' && (
                              <span className="text-[10px] text-[#6B4E8A] font-medium" data-testid={`tier-${u.user_id}`}>
                                {u.plan_tier}
                              </span>
                            )}
                            {u.locked_rate_label && (
                              <span className="text-[10px] text-[#8DB5A0] italic" data-testid={`lockedrate-${u.user_id}`}>
                                {u.locked_rate_label}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-4">
                        {/* Founder-only: Admin ON/OFF toggle. Founder account itself is locked. */}
                        {isFounder && u.admin_role !== 'founder' && (
                          <div
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FAF7F2] border border-[#E5DFEC]"
                            data-testid={`admin-toggle-wrap-${u.user_id}`}
                          >
                            <Switch
                              checked={u.admin_role === 'admin' || u.is_admin}
                              onCheckedChange={() => requestAdminToggle(u)}
                              disabled={processingId === u.user_id}
                              data-testid={`admin-toggle-${u.user_id}`}
                            />
                            <span
                              className={`text-xs font-semibold uppercase tracking-wider ${
                                (u.admin_role === 'admin' || u.is_admin) ? 'text-[#6B4E8A]' : 'text-[#94A3B8]'
                              }`}
                              data-testid={`admin-toggle-label-${u.user_id}`}
                            >
                              {(u.admin_role === 'admin' || u.is_admin) ? 'Admin · ON' : 'Member · OFF'}
                            </span>
                          </div>
                        )}
                        {isFounder && u.admin_role !== 'founder' && u.is_admin && (
                          <div
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FAF7F2] border border-[#E5DFEC]"
                            title="Allow this admin to edit & delete other members' posts, events, meetups, wage entries and polls"
                            data-testid={`moderate-toggle-wrap-${u.user_id}`}
                          >
                            <Switch
                              checked={!!u.can_moderate_content}
                              onCheckedChange={() => toggleModerateContent(u)}
                              className="data-[state=checked]:bg-[#6B4E8A] data-[state=unchecked]:bg-[#CBD5E1]"
                              disabled={processingId === u.user_id}
                              data-testid={`moderate-toggle-${u.user_id}`}
                            />
                            <span
                              className={`text-xs font-semibold uppercase tracking-wider ${u.can_moderate_content ? 'text-[#6B4E8A]' : 'text-[#94A3B8]'}`}
                              data-testid={`moderate-toggle-label-${u.user_id}`}
                            >
                              {u.can_moderate_content ? 'Moderate · ON' : 'Moderate · OFF'}
                            </span>
                          </div>
                        )}
                        {u.admin_role !== 'founder' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRoleDialog(u)}
                              className="rounded-full text-xs"
                              data-testid={`set-role-${u.user_id}`}
                            >
                              <UserCog className="w-3 h-3 mr-1" />
                              Role
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openResetPwDialog(u)}
                              className="rounded-full text-xs"
                              data-testid={`reset-pw-${u.user_id}`}
                            >
                              <KeyRound className="w-3 h-3 mr-1" />
                              Password
                            </Button>
                          </>
                        )}
                        {u.is_banned && (
                          <Button
                            size="sm"
                            onClick={() => handleUnbanUser(u.user_id)}
                            className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full text-xs"
                            disabled={processingId === u.user_id}
                          >
                            Unban
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                      <p className="text-[#64748B]">{userSearch ? "No users matching search" : "No users found"}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="hygiena" data-testid="hygiena-admin-content">
                <HygienaAdminPanel />
              </TabsContent>

              <TabsContent value="taxonomy" data-testid="taxonomy-admin-content">
                <div className="space-y-4">
                  <div>
                    <h2 className="text-xl font-medium text-[#6B4E8A] mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Lists & Categories</h2>
                    <p className="text-sm text-[#64748B]">
                      Every dropdown / category list on the platform. Add or remove items here, or use the gear icon inline next to each list on its own page. Deletions are soft so historical content keeps rendering — restore at any time.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { kind: "discussion_categories", title: "Discussion categories", description: "Shown in the Discussions filter dropdown." },
                      { kind: "expense_categories", title: "Financial Organizer expense categories", description: "CRA-aligned categories for expense entries." },
                      { kind: "mileage_categories", title: "Mileage Tracker purpose tags", description: "Purposes members can tag a trip with." },
                      { kind: "partner_specialties", title: "Partner specialties", description: "Categories on Preferred Partners listings." },
                      { kind: "mentorship_topics", title: "Mentorship topics", description: "Topics mentors and mentees pick from." },
                      { kind: "clinic_rating_criteria", title: "Clinic rating criteria", description: "Yes/No/Sometimes questions on the rating form." },
                      { kind: "wage_form_fields", title: "Wage Survey form fields", description: "Compensation fields shown on the wage entry form." },
                      { kind: "profile_roles", title: "Professional Role dropdown", description: "Roles on member profile." },
                      { kind: "profile_certifications", title: "Certifications/Credentials", description: "Credentials members can claim on their profile." },
                    ].map(t => (
                      <div key={t.kind} className="bg-white border border-[#E5DFEC] rounded-2xl p-4 flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>{t.title}</h3>
                          <p className="text-xs text-[#64748B] mt-1">{t.description}</p>
                        </div>
                        <TaxonomyManager
                          kind={t.kind}
                          title={t.title}
                          description={t.description}
                          compact={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="whats_new" data-testid="whats-new-admin-content">
                <WhatsNewPublisher />
              </TabsContent>

              {canModerateContent && (
                <TabsContent value="reports" data-testid="reports-admin-content">
                  <ReportsQueuePanel onChange={fetchReportCount} />
                </TabsContent>
              )}

              <TabsContent value="polls" data-testid="polls-admin-content">
                <PollsAdminPanel />
              </TabsContent>
          </Tabs>
        </motion.div>

        {/* Role Assignment Dialog */}
        <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Assign Role — {selectedUser?.name || selectedUser?.email}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Role</Label>
                <div className="flex gap-2 mt-2">
                  {["moderator", "admin"].map((role) => (
                    <Button
                      key={role}
                      type="button"
                      variant={roleForm.admin_role === role ? "default" : "outline"}
                      onClick={() => {
                        const presets = role === "admin" 
                          ? Object.keys(PERMISSION_LABELS) 
                          : ["approve_verifications", "approve_posts", "view_admin_stats", "view_alerts"];
                        setRoleForm({ admin_role: role, permissions: presets });
                      }}
                      className={`rounded-full capitalize ${roleForm.admin_role === role ? 'bg-[#6B4E8A] text-white' : ''}`}
                      data-testid={`role-option-${role}`}
                    >
                      {role}
                    </Button>
                  ))}
                </div>
              </div>
              {roleForm.admin_role && (
                <div>
                  <Label className="mb-3 block">Permissions</Label>
                  <div className="space-y-3">
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox
                          id={`perm-${key}`}
                          checked={roleForm.permissions.includes(key)}
                          onCheckedChange={() => togglePermission(key)}
                        />
                        <Label htmlFor={`perm-${key}`} className="cursor-pointer text-sm text-[#6B4E8A]">
                          {label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-between gap-3 pt-4">
                {selectedUser?.admin_role && selectedUser?.admin_role !== "founder" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRemoveRole}
                    className="rounded-full border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                    disabled={processingId === selectedUser?.user_id}
                  >
                    Remove Role
                  </Button>
                )}
                <div className="flex gap-3 ml-auto">
                  <Button type="button" variant="outline" onClick={() => setShowRoleDialog(false)} className="rounded-full">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSetRole}
                    className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                    disabled={!roleForm.admin_role || processingId === selectedUser?.user_id}
                    data-testid="confirm-role-btn"
                  >
                    Save Role
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showResetPwDialog} onOpenChange={setShowResetPwDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Reset Password
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-[#64748B]">
                Reset password for <strong className="text-[#6B4E8A]">{selectedUser?.email}</strong>
              </p>
              <div>
                <Label htmlFor="new-pw">New Password</Label>
                <Input
                  id="new-pw"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="mt-2 bg-white border-[#E5DFEC]"
                  data-testid="new-password-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowResetPwDialog(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button
                  onClick={handleResetPassword}
                  className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full"
                  disabled={newPassword.length < 6 || processingId === selectedUser?.user_id}
                  data-testid="confirm-reset-pw-btn"
                >
                  Reset Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Founder-only Admin toggle confirmation */}
        <AlertDialog
          open={!!adminToggleTarget}
          onOpenChange={(open) => { if (!open) setAdminToggleTarget(null); }}
        >
          <AlertDialogContent data-testid="admin-toggle-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {adminToggleTarget?.willBeAdmin ? "Promote to admin?" : "Revoke admin access?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {adminToggleTarget?.willBeAdmin ? (
                  <>
                    <strong>{adminToggleTarget?.user?.name || adminToggleTarget?.user?.email}</strong> will gain
                    full admin permissions: managing members, content moderation, verifications, and password resets.
                    They will not be able to modify your founder account or other admins' roles.
                  </>
                ) : (
                  <>
                    <strong>{adminToggleTarget?.user?.name || adminToggleTarget?.user?.email}</strong> will lose
                    all admin permissions and become a regular member. This action will be logged in the audit trail.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={adminTogglePending} data-testid="admin-toggle-cancel-btn">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmAdminToggle}
                disabled={adminTogglePending}
                className={adminToggleTarget?.willBeAdmin
                  ? "bg-[#6B4E8A] hover:bg-[#553D6F] text-white"
                  : "bg-[#DC2626] hover:bg-[#B91C1C] text-white"}
                data-testid="admin-toggle-confirm-btn"
              >
                {adminTogglePending
                  ? "Working..."
                  : adminToggleTarget?.willBeAdmin ? "Yes, make admin" : "Yes, revoke admin"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create Guest Professional modal (founder-only). Stays open after
            create so the founder can copy the auto-generated password before
            dismissing. Closing resets all state. */}
        <Dialog
          open={showGuestProModal}
          onOpenChange={(open) => {
            setShowGuestProModal(open);
            if (!open) setLastCreatedGuestPro(null);
          }}
        >
          <DialogContent className="bg-[#FAF7F2] max-w-md" data-testid="create-guest-pro-modal">
            <DialogHeader>
              <DialogTitle className="text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {lastCreatedGuestPro ? "Account created" : "Create Guest Professional Account"}
              </DialogTitle>
            </DialogHeader>

            {lastCreatedGuestPro ? (
              <div className="space-y-3">
                <div className="bg-white border border-[#E5DFEC] rounded-xl p-4 space-y-2 text-sm">
                  <p><strong className="text-[#6B4E8A]">{lastCreatedGuestPro.full_name}</strong> · {lastCreatedGuestPro.email}</p>
                  <p className="text-[#475569]">{lastCreatedGuestPro.company_or_firm} · {lastCreatedGuestPro.reason}</p>
                  <div className="bg-[#F0ECF7] rounded-lg p-3 font-mono text-xs break-all">
                    <p className="text-[#64748B] mb-1">Temporary password:</p>
                    <p className="text-[#0F172A] select-all" data-testid="created-guest-pro-password">{lastCreatedGuestPro.temporary_password}</p>
                  </div>
                  <p className="text-xs text-[#64748B] mt-2">
                    {lastCreatedGuestPro.welcome_email_sent
                      ? `Welcome email with these credentials was sent to ${lastCreatedGuestPro.email}.`
                      : "Welcome email was NOT sent. Copy the password above and share it manually."}
                  </p>
                </div>
                <Button onClick={() => { setShowGuestProModal(false); setLastCreatedGuestPro(null); }} className="w-full bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full">
                  Done
                </Button>
              </div>
            ) : (
              <form onSubmit={handleCreateGuestPro} className="space-y-3">
                <div>
                  <Label htmlFor="gp_full_name">Full name *</Label>
                  <Input id="gp_full_name" value={guestProForm.full_name} onChange={e => setGuestProForm({...guestProForm, full_name: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="gp-full-name" required />
                </div>
                <div>
                  <Label htmlFor="gp_email">Email *</Label>
                  <Input id="gp_email" type="email" value={guestProForm.email} onChange={e => setGuestProForm({...guestProForm, email: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="gp-email" required />
                </div>
                <div>
                  <Label htmlFor="gp_company">Company or firm *</Label>
                  <Input id="gp_company" value={guestProForm.company_or_firm} onChange={e => setGuestProForm({...guestProForm, company_or_firm: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" placeholder="e.g. Smith & Associates CPAs" data-testid="gp-company" required />
                </div>
                <div>
                  <Label htmlFor="gp_reason">Reason for access *</Label>
                  <Input id="gp_reason" value={guestProForm.reason} onChange={e => setGuestProForm({...guestProForm, reason: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" placeholder="e.g. accountant, lawyer, business consultant" data-testid="gp-reason" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="gp_start">Access starts</Label>
                    <Input id="gp_start" type="date" value={guestProForm.access_start_date} onChange={e => setGuestProForm({...guestProForm, access_start_date: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="gp-start" />
                    <p className="text-[10px] text-[#94A3B8] mt-1">Defaults to today</p>
                  </div>
                  <div>
                    <Label htmlFor="gp_end">Access ends (optional)</Label>
                    <Input id="gp_end" type="date" value={guestProForm.access_end_date} onChange={e => setGuestProForm({...guestProForm, access_end_date: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="gp-end" />
                    <p className="text-[10px] text-[#94A3B8] mt-1">Indefinite if blank</p>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#475569] pt-1">
                  <Checkbox checked={guestProForm.send_welcome_email} onCheckedChange={(c) => setGuestProForm({...guestProForm, send_welcome_email: !!c})} data-testid="gp-send-email" />
                  Send welcome email with login credentials
                </label>
                <Button type="submit" disabled={creatingGuestPro} className="w-full bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full" data-testid="gp-submit">
                  {creatingGuestPro ? "Creating..." : "Create account"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminPage;
